import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Flex, Button, Text } from '@chakra-ui/react';
import { EmailComposeBlock } from './EmailComposeBlock';
import type { EmailComposeBlockData } from '../../../types';

interface EmailComposeGroupProps {
  drafts: EmailComposeBlockData[];
}

// Wraps the per-candidate composers produced when several candidates were
// selected (one personalized draft each). Shows a "N drafts" header with a
// "Send all" affordance, while each composer is still reviewed/edited/sent on
// its own. A single draft renders bare (no group chrome).
export const EmailComposeGroup = ({ drafts }: EmailComposeGroupProps) => {
  const sendsRef = useRef<Map<string, () => void>>(new Map());
  const registerSend = useCallback((key: string, fn: (() => void) | null) => {
    if (fn) sendsRef.current.set(key, fn);
    else sendsRef.current.delete(key);
  }, []);

  // Drafts may already be sent (reload after sending some) — seed the count.
  const initialSent = useMemo(
    () => drafts.filter((d) => d.sent).length,
    [drafts]
  );
  const [sentCount, setSentCount] = useState(initialSent);
  const onSent = useCallback(() => setSentCount((c) => c + 1), []);

  if (drafts.length <= 1) {
    return drafts[0] ? <EmailComposeBlock data={drafts[0]} /> : null;
  }

  const total = drafts.length;
  const allSent = sentCount >= total;

  return (
    <Box>
      <Flex align="center" justify="space-between" mb="10px" px="4px" gap="8px">
        <Text fontSize="sm" fontWeight="semibold" color="secondary-900">
          {total} personalized drafts
          {sentCount > 0
            ? ` · ${sentCount}/${total} sent`
            : ' — review each below'}
        </Text>
        <Button
          variant="primary"
          size="sm"
          isDisabled={allSent}
          onClick={() => sendsRef.current.forEach((fn) => fn())}
        >
          {allSent ? 'All sent' : `Send all (${total - sentCount})`}
        </Button>
      </Flex>
      <Flex direction="column" gap="12px">
        {drafts.map((d, i) => (
          <EmailComposeBlock
            key={d.toolCallId ?? i}
            data={d}
            registerSend={registerSend}
            onSent={onSent}
          />
        ))}
      </Flex>
    </Box>
  );
};
